import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { map, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AggregierteAuswertungLayoutApiService } from '../../api/services/aggregierte-auswertung-layout-api.service';
import { MonatsauswertungLayoutApiService } from '../../api/services/monatsauswertung-layout-api.service';
import { AuswertungLayoutsActions } from './auswertung-layouts.actions';

@Injectable()
export class AuswertungLayoutsEffects {
  loadAuswertungLayouts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        AuswertungLayoutsActions.loadMonatsauswertungLayouts,
        AuswertungLayoutsActions.loadMonatsauswertungLayoutsUndSpalten,
      ),
      /** An EMPTY observable only emits completion. Replace with your own observable API request */
      switchMap(() =>
        this.monatsauswertungen_api$.getAllMonatsauswertungLayouts$Json().pipe(
          map((layouts) =>
            AuswertungLayoutsActions.loadMonatsauswertungLayoutsSuccess({
              monatsauswertungLayouts: layouts,
            }),
          ),
          catchError((error) =>
            of(
              AuswertungLayoutsActions.loadMonatsauswertungLayoutsFailure({
                error,
              }),
            ),
          ),
        ),
      ),
    );
  });

  loadAuswertungSpalten$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        AuswertungLayoutsActions.loadMonatsauswertungSpalten,
        AuswertungLayoutsActions.loadMonatsauswertungLayoutsUndSpalten,
      ),
      switchMap(() =>
        this.monatsauswertungen_api$.getAllMonatsauswertungSpalten$Json().pipe(
          map((spalten) =>
            AuswertungLayoutsActions.loadMonatsauswertungSpaltenSuccess({
              monatsauswertungSpalten: spalten,
            }),
          ),
          catchError((error) =>
            of(
              AuswertungLayoutsActions.loadMonatsauswertungSpaltenFailure({
                error,
              }),
            ),
          ),
        ),
      ),
    );
  });

  createMonatsauswertungLayout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuswertungLayoutsActions.createMonatsauswertungLayout),
      switchMap(({ monatsauswertungLayout }) =>
        this.monatsauswertungen_api$
          .createMonatsauswertungLayout$Json({
            body: monatsauswertungLayout,
          })
          .pipe(
            map((layout) =>
              AuswertungLayoutsActions.createMonatsauswertungLayoutSuccess({
                monatsauswertungLayout: layout,
              }),
            ),
            catchError((error) =>
              of(
                AuswertungLayoutsActions.createMonatsauswertungLayoutFailure({
                  error,
                }),
              ),
            ),
          ),
      ),
    );
  });

  updateMonatsauswertungLayout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuswertungLayoutsActions.updateMonatsauswertungLayout),
      switchMap(({ monatsauswertungLayout, id }) =>
        this.monatsauswertungen_api$
          .updateMonatsauswertungLayout$Json({
            id,
            body: monatsauswertungLayout,
          })
          .pipe(
            map((layout) =>
              AuswertungLayoutsActions.updateMonatsauswertungLayoutSuccess({
                monatsauswertungLayout: layout,
              }),
            ),
            catchError((error) =>
              of(
                AuswertungLayoutsActions.updateMonatsauswertungLayoutFailure({
                  error,
                }),
              ),
            ),
          ),
      ),
    );
  });

  loadAggregierteAuswertungenLayouts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        AuswertungLayoutsActions.loadAggregierteAuswertungenLayouts,
        AuswertungLayoutsActions.loadAggregierteAuswertungenLayoutsUndSpalten,
      ),
      switchMap(() =>
        this.aggregierte_auswertungen_api$.getAllAggregierteAuswertungLayouts$Json().pipe(
          map((layouts) =>
            AuswertungLayoutsActions.loadAggregierteAuswertungenLayoutsSuccess({
              aggregierteAuswertungenLayouts: layouts,
            }),
          ),
          catchError((error) =>
            of(
              AuswertungLayoutsActions.loadAggregierteAuswertungenLayoutsFailure({
                error,
              }),
            ),
          ),
        ),
      ),
    );
  });

  loadAggregierteAuswertungenSpalten$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        AuswertungLayoutsActions.loadAggregierteAuswertungenSpalten,
        AuswertungLayoutsActions.loadAggregierteAuswertungenLayoutsUndSpalten,
      ),
      switchMap(() =>
        this.aggregierte_auswertungen_api$.getAllAggregierteAuswertungSpalten$Json().pipe(
          map((spalten) =>
            AuswertungLayoutsActions.loadAggregierteAuswertungenSpaltenSuccess({
              aggregierteAuswertungenSpalten: spalten,
            }),
          ),
          catchError((error) =>
            of(
              AuswertungLayoutsActions.loadAggregierteAuswertungenSpaltenFailure({
                error,
              }),
            ),
          ),
        ),
      ),
    );
  });

  createAggregierteAuswertungLayout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuswertungLayoutsActions.createAggregierteAuswertungenLayout),
      switchMap(({ aggregierteAuswertungenLayout }) =>
        this.aggregierte_auswertungen_api$
          .createAggregierteAuswertungLayout$Json({
            body: aggregierteAuswertungenLayout,
          })
          .pipe(
            map((layout) =>
              AuswertungLayoutsActions.createAggregierteAuswertungenLayoutSuccess({
                aggregierteAuswertungenLayout: layout,
              }),
            ),
            catchError((error) =>
              of(
                AuswertungLayoutsActions.createAggregierteAuswertungenLayoutFailure({
                  error,
                }),
              ),
            ),
          ),
      ),
    );
  });

  updateAggregierteAuswertungLayout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuswertungLayoutsActions.updateAggregierteAuswertungenLayout),
      switchMap(({ aggregierteAuswertungenLayout, id }) =>
        this.aggregierte_auswertungen_api$
          .updateAggregierteAuswertungLayout$Json({
            id,
            body: aggregierteAuswertungenLayout,
          })
          .pipe(
            map((layout) =>
              AuswertungLayoutsActions.updateAggregierteAuswertungenLayoutSuccess({
                aggregierteAuswertungenLayout: layout,
              }),
            ),
            catchError((error) =>
              of(
                AuswertungLayoutsActions.updateAggregierteAuswertungenLayoutFailure({
                  error,
                }),
              ),
            ),
          ),
      ),
    );
  });

  constructor(
    private actions$: Actions,
    private monatsauswertungen_api$: MonatsauswertungLayoutApiService,
    private aggregierte_auswertungen_api$: AggregierteAuswertungLayoutApiService
  ) {}
}
